import { log } from '@/lib/logger'
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, Link as LinkIcon, Settings2, Unlink } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AutomationSettingsLinkAlert } from '@/components/settings/AutomationSettingsLinkAlert';
import { AUTOMATION_TEMPLATE_UI } from '@/components/settings/automationTemplatesUi';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/hooks/use-auth';
import { useFacebookStatusQuery } from '@/queries';
import { FormLinkDialog } from './FormLinkDialog';
import {
  FacebookTemplateMappingEditDialog,
  type FacebookTemplateLinkRecord,
} from './FacebookTemplateMappingEditDialog';

interface Page {
    id: string;
    name: string;
    access_token?: string;
}

interface LeadgenForm {
    id: string;
    name: string;
    status: string;
    locale: string;
    questions?: Array<{
        key: string;
        label: string;
        type: string;
    }>;
    created_time?: string;
}

interface FormLink extends FacebookTemplateLinkRecord {
    form_id: string;
    form_name?: string;
    page_id?: string;
    is_active: boolean;
}

export function InstantFormsList() {
    const { t } = useTranslation();
    const { lang } = useParams<{ lang: string }>();
    const { token } = useAuth();
    const hasAuthToken = Boolean(token && token.trim());
    const {
        data: facebookStatus,
        error: facebookStatusError,
        isLoading: loadingFacebookStatus,
    } = useFacebookStatusQuery({ enabled: hasAuthToken });
    const [pages, setPages] = useState<Page[]>([]);
    const [selectedPage, setSelectedPage] = useState<string>('');
    const [forms, setForms] = useState<LeadgenForm[]>([]);
    const [loadingPages, setLoadingPages] = useState(false);
    const [loadingForms, setLoadingForms] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [needsAuth, setNeedsAuth] = useState(false);
    const [needsReconnect, setNeedsReconnect] = useState(false);

    // Linking state
    const [existingLinks, setExistingLinks] = useState<Map<string, FormLink>>(new Map());
    const [linkingForm, setLinkingForm] = useState<LeadgenForm | null>(null);
    const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
    const [editingMapping, setEditingMapping] = useState<{ link: FormLink; form: LeadgenForm } | null>(null);
    const [isEditMappingOpen, setIsEditMappingOpen] = useState(false);
    const [togglingFormId, setTogglingFormId] = useState<string | null>(null);

    const fetchExistingLinks = React.useCallback(async () => {
        try {
            if (!token || !token.trim()) {
                console.error('No token available for fetching links');
                return;
            }
            
            const response = await fetch('/api/v1/leads/meta/form-links/', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const links: FormLink[] = await response.json();
                const linksMap = new Map<string, FormLink>();
                links.forEach(link => linksMap.set(link.form_id, link));
                setExistingLinks(linksMap);
            }
        } catch (err) {
            console.error("Failed to fetch existing links", err);
        }
    }, [token]);

    // Fetch Pages on mount
    useEffect(() => {
        if (!hasAuthToken) {
            return;
        }

        if (loadingFacebookStatus) {
            return;
        }

        if (facebookStatus?.connected === false || facebookStatus?.needs_reconnect === true) {
            setNeedsAuth(true);
            setNeedsReconnect(facebookStatus?.needs_reconnect === true);
            setError(null);
            setPages([]);
            setSelectedPage('');
            setForms([]);
            return;
        }

        if (facebookStatusError) {
            setNeedsAuth(false);
            setNeedsReconnect(false);
            setError(facebookStatusError instanceof Error ? facebookStatusError.message : t('error.generic'));
            return;
        }

        if (!facebookStatus?.connected) {
            return;
        }

        let isActive = true;

        const fetchPages = async () => {
            setLoadingPages(true);
            setError(null);
            setNeedsAuth(false);
            setNeedsReconnect(false);
            try {
                if (!token || !token.trim()) {
                    console.error('No token available for authentication');
                    setError('Authentication token not available');
                    return;
                }
                
                log.debug('Fetching pages with token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');
                const response = await fetch('/api/v1/leads/meta/pages/', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    let errorMessage = '';
                    let errorDetails = '';
                    try {
                        const errorData = await response.json();
                        if (errorData?.needs_reconnect) {
                            setNeedsAuth(true);
                            setNeedsReconnect(true);
                            setError(null);
                            setPages([]);
                            setSelectedPage('');
                            setForms([]);
                            return;
                        }
                        errorMessage = errorData?.error || errorData?.message || '';
                        errorDetails = JSON.stringify(errorData);
                    } catch {
                        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                    }
                    
                    console.error('Failed to fetch pages:', errorMessage, errorDetails);
                    
                    if (errorMessage.toLowerCase().includes('token') || 
                        errorMessage.toLowerCase().includes('connect') ||
                        errorMessage.toLowerCase().includes('facebook account') ||
                        errorMessage.toLowerCase().includes('authentication required') ||
                        errorMessage.toLowerCase().includes('meta access token')) {
                        setNeedsAuth(true);
                        setNeedsReconnect(false);
                        setError(null);
                        return;
                    }
                    
                    throw new Error(errorMessage || `Failed to fetch pages: ${response.status}`);
                }

                const data = await response.json();
                if (!isActive) {
                    return;
                }
                log.debug('Pages data received:', data);
                setPages(data.data || []);

                if (data.data && data.data.length > 0) {
                    setSelectedPage(data.data[0].id);
                } else {
                    setError('No pages found. Please ensure your Facebook account has pages.');
                }
            } catch (err) {
                console.error('Error fetching pages:', err);
                if (!needsAuth) {
                    setError(err instanceof Error ? err.message : t('error.generic'));
                }
            } finally {
                if (isActive) {
                    setLoadingPages(false);
                }
            }
        };

        if (token && token.trim()) {
            fetchPages();
            fetchExistingLinks();
        }

        return () => {
            isActive = false;
        };
    }, [facebookStatus?.connected, facebookStatus?.needs_reconnect, facebookStatusError, fetchExistingLinks, hasAuthToken, loadingFacebookStatus, t, token]);

    // Fetch Forms when page changes
    useEffect(() => {
        const fetchForms = async () => {
            if (!selectedPage) {
                setForms([]);
                return;
            }

            setLoadingForms(true);
            setError(null);
            try {
                if (!token || !token.trim()) {
                    console.error('No token available for fetching forms');
                    setError('Authentication token not available');
                    return;
                }
                
                const response = await fetch(`/api/v1/leads/meta/leadgen-forms/?page_id=${selectedPage}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    let errorMessage = '';
                    try {
                        const errorData = await response.json();
                        if (errorData?.needs_reconnect) {
                            setNeedsAuth(true);
                            setNeedsReconnect(true);
                            setError(null);
                            setForms([]);
                            setSelectedPage('');
                            return;
                        }
                        errorMessage = errorData?.error || errorData?.message || '';
                    } catch {
                        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                    }
                    console.error('Failed to fetch forms:', errorMessage);
                    throw new Error(errorMessage || `Failed to fetch forms: ${response.status}`);
                }

                const data = await response.json();
                log.debug('Forms data received:', data);
                setForms(data.data || []);
            } catch (err) {
                console.error('Error fetching forms:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch forms');
            } finally {
                setLoadingForms(false);
            }
        };

        if (token && token.trim()) {
            fetchForms();
        }
    }, [selectedPage, token]);

    const openLinkDialog = (form: LeadgenForm) => {
        setLinkingForm(form);
        setIsLinkDialogOpen(true);
    };

    const handleLinkSuccess = () => {
        fetchExistingLinks();
    };

    const handleUnlink = async (formId: string) => {
        if (!confirm("Are you sure you want to unlink this template?")) return;

        try {
            const response = await fetch(`/api/v1/leads/meta/form-links/?form_id=${formId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                fetchExistingLinks();
            } else {
                throw new Error("Failed to unlink");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const openEditMappingDialog = (form: LeadgenForm, link: FormLink) => {
        setEditingMapping({ form, link });
        setIsEditMappingOpen(true);
    };

    const handleToggleActivation = async (formId: string, isActive: boolean) => {
        setTogglingFormId(formId);
        setError(null);
        try {
            const response = await fetch(`/api/v1/leads/meta/form-links/?form_id=${formId}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ is_active: isActive }),
            });

            if (!response.ok) {
                throw new Error('Failed to update form automation status');
            }

            await fetchExistingLinks();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update form automation status');
        } finally {
            setTogglingFormId(null);
        }
    };

    const getQuestionsCount = (form: LeadgenForm): number => {
        return form.questions?.length || 0;
    };

    const hasPhoneField = (form: LeadgenForm): boolean => {
        return form.questions?.some(q => q.type === 'PHONE' || q.type === 'phone_number') || false;
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex justify-between items-center">
                    Instant Forms
                    {(loadingPages || loadingFacebookStatus) && <Loader2 className="h-4 w-4 animate-spin" />}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {needsAuth && lang ? (
                    <AutomationSettingsLinkAlert
                        lang={lang}
                        message={
                            needsReconnect || facebookStatus?.needs_reconnect
                                ? AUTOMATION_TEMPLATE_UI.facebookReconnectRequiredMessage
                                : AUTOMATION_TEMPLATE_UI.facebookRequiredMessage
                        }
                    />
                ) : null}
                {error && !needsAuth && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">Page:</span>
                    <Select value={selectedPage} onValueChange={setSelectedPage} disabled={loadingPages || loadingFacebookStatus || needsAuth}>
                        <SelectTrigger className="w-[280px]">
                            <SelectValue placeholder="Select Page" />
                        </SelectTrigger>
                        <SelectContent>
                            {pages.map((page) => (
                                <SelectItem key={page.id} value={page.id}>
                                    {page.name} ({page.id})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Locale</TableHead>
                                <TableHead>Questions</TableHead>
                                <TableHead>Linked Template</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loadingForms ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-4">
                                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                                    </TableCell>
                                </TableRow>
                            ) : forms.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-4 text-muted-foreground">
                                        No instant forms found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                forms.map((form) => {
                                    const link = existingLinks.get(form.id);
                                    const questionsCount = getQuestionsCount(form);
                                    const phoneAvailable = hasPhoneField(form);
                                    return (
                                        <TableRow key={form.id}>
                                            <TableCell className="font-medium">
                                                <div className="flex items-center gap-2">
                                                    {form.name}
                                                    {!phoneAvailable && (
                                                        <Badge variant="outline" className="text-xs">
                                                            No phone
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="text-xs text-muted-foreground">{form.id}</div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={form.status === 'ACTIVE' ? 'default' : 'secondary'}>
                                                    {form.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>{form.locale}</TableCell>
                                            <TableCell>{questionsCount}</TableCell>
                                            <TableCell>
                                                {link ? (
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <Badge variant="outline" className="flex items-center w-fit gap-1">
                                                            <LinkIcon className="h-3 w-3" />
                                                            {link.template_name}
                                                        </Badge>
                                                        <Badge variant={link.is_active ? 'default' : 'secondary'}>
                                                            {link.is_active ? 'Active' : 'Paused'}
                                                        </Badge>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted-foreground text-sm">-</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {link ? (
                                                    <div className="flex items-center justify-end gap-2">
                                                        <Switch
                                                            checked={link.is_active}
                                                            onCheckedChange={(checked) => void handleToggleActivation(form.id, checked)}
                                                            disabled={togglingFormId === form.id}
                                                            aria-label={`Toggle form automation ${form.name}`}
                                                        />
                                                        <Button
                                                            variant="ghost"
                                                            size="icon-xs"
                                                           
                                                            onClick={() => openEditMappingDialog(form, link)}
                                                            title="Edit variable mapping"
                                                        >
                                                            <Settings2 className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon-xs"
                                                            className="text-destructive"
                                                            onClick={() => handleUnlink(form.id)}
                                                            title="Unlink Template"
                                                        >
                                                            <Unlink className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => openLinkDialog(form)}
                                                        disabled={!phoneAvailable}
                                                        title={!phoneAvailable ? "Form must have a phone field" : "Link Template"}
                                                    >
                                                        Link Template
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>

            <FormLinkDialog
                form={linkingForm}
                pageId={selectedPage}
                isOpen={isLinkDialogOpen}
                onClose={() => setIsLinkDialogOpen(false)}
                onLinkSuccess={handleLinkSuccess}
            />

            <FacebookTemplateMappingEditDialog
                target="form"
                link={editingMapping?.link ?? null}
                formQuestions={editingMapping?.form.questions}
                isOpen={isEditMappingOpen}
                onClose={() => {
                    setIsEditMappingOpen(false);
                    setEditingMapping(null);
                }}
                onSuccess={fetchExistingLinks}
            />
        </Card>
    );
}

